import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { chProjectService } from '../services';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function ChProjectForm() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const isEdit = slug && slug !== 'novo';

  const [form, setForm] = useState({
    name: '',
    slug: '',
    workcode: '',
    tone_profile: '',
    archived: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      chProjectService.getBySlug(slug).then(project => {
        if (project) {
          setForm({
            name: project.name,
            slug: project.slug,
            workcode: project.workcode || '',
            tone_profile: project.tone_profile || '',
            archived: project.archived,
          });
          setProjectId(project.id);
        }
        setLoading(false);
      });
    }
  }, [slug, isEdit]);

  const handleNameChange = (name: string) => {
    setForm(f => ({
      ...f,
      name,
      slug: isEdit ? f.slug : slugify(name),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEdit && projectId) {
        await chProjectService.update(projectId, {
          name: form.name,
          slug: form.slug,
          workcode: form.workcode || null,
          tone_profile: form.tone_profile || null,
          archived: form.archived,
        });
        navigate(`/contenthub/projetos/${form.slug}`);
      } else {
        const project = await chProjectService.create({
          name: form.name,
          slug: form.slug,
          workcode: form.workcode || null,
          tone_profile: form.tone_profile || null,
          archived: false,
        });
        navigate(`/contenthub/projetos/${project.slug}`);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar projeto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-2xl mx-auto">
        <div className="ch-card animate-pulse space-y-4">
          <div className="h-6 w-40 bg-gray-100 rounded" />
          <div className="h-10 bg-gray-50 rounded-xl" />
          <div className="h-10 bg-gray-50 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto ch-animate-in">
      <button
        onClick={() => navigate(-1)}
        className="ch-btn ch-btn-ghost ch-btn-sm mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </button>

      <h1 className="ch-heading ch-heading-lg mb-6">
        {isEdit ? 'Editar Projeto' : 'Novo Projeto'}
      </h1>

      <form onSubmit={handleSubmit} className="ch-card space-y-5">
        {error && (
          <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium">
            {error}
          </div>
        )}

        <div>
          <label className="ch-label">Nome do Projeto *</label>
          <input
            type="text"
            className="ch-input"
            value={form.name}
            onChange={e => handleNameChange(e.target.value)}
            placeholder="Ex: Festival do Crato"
            required
          />
        </div>

        <div>
          <label className="ch-label">Slug (URL)</label>
          <input
            type="text"
            className="ch-input font-mono text-sm"
            value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
            placeholder="festival-do-crato"
            required
          />
          <p className="text-xs text-gray-400 mt-1">Identificador único na URL</p>
        </div>

        <div>
          <label className="ch-label">Código de Campanha</label>
          <input
            type="text"
            className="ch-input"
            value={form.workcode}
            onChange={e => setForm(f => ({ ...f, workcode: e.target.value }))}
            placeholder="MOHIT-2026-001"
          />
          <p className="text-xs text-gray-400 mt-1">
            Liga este projeto à app de gestão de campanhas
          </p>
        </div>

        <div>
          <label className="ch-label">Tom e Voz do Projeto</label>
          <textarea
            className="ch-textarea"
            value={form.tone_profile}
            onChange={e => setForm(f => ({ ...f, tone_profile: e.target.value }))}
            placeholder="Descreve o tom, voz e estilo do projeto. Esta informação será usada pela camada de IA para gerar conteúdo adequado..."
            rows={5}
          />
          <p className="text-xs text-gray-400 mt-1">
            Será usado pela camada de IA para gerar conteúdo adequado
          </p>
        </div>

        {isEdit && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50">
            <input
              type="checkbox"
              id="archived"
              checked={form.archived}
              onChange={e => setForm(f => ({ ...f, archived: e.target.checked }))}
              className="w-4 h-4 rounded"
            />
            <label htmlFor="archived" className="text-sm text-gray-700">
              Arquivar este projeto
            </label>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving}
            className="ch-btn ch-btn-primary"
          >
            <Save className="w-4 h-4" />
            {saving ? 'A guardar...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
